<script setup lang="ts">
import { onClickOutside, useCurrentElement, useResizeObserver } from '@vueuse/core'
import { removeBy } from '@shared/array'
import { removeDecimals } from '@consumer/helpers/number'
import { scrollIntoView } from '@shared/scroll'
import { identity } from '@shared/functions'
import { isPresent, isEqual, isNil, isObject } from '@consumer/helpers/object'
import { filterItems } from '@consumer/services/filter'
import { computePosition, flip, offset } from '@floating-ui/dom'
import { typeaheadDebounce } from '@shared/debounce'
import type { PathHelper } from '@corp/services/api'
import { faChevronDown } from '@fortawesome/pro-light-svg-icons'
import type GTextInput from './GTextInput.vue'

const {
  modelValue: value,
  options,
  multi,
  disabled,
  required,
  unselectLabel = 'Unselect',
  noUnselect,
  wholeItem,
  allowCreate,
  labelForValue: originalLabelForValue,
  size = 'large',
  pill = false,
  labelProp = 'label',
  valueProp = 'value',
  searchableProp,
  descriptionProp = 'description',
  selectedProp = null,
  numeric,
  placeholder: originalPlaceholder = 'Please Choose One',
  fetchOptions,
  collapseOnSelect = true,
  emptyQueryOptions = [],
  includeSelectedInOptions = false,
} = defineProps<{
  // Options that can be selected and will be displayed in the dropdown.
  options?: any[]

  // Selected item or list of selected items that populate the input.
  modelValue?: any

  // The size of the input.
  size?: 'small' | 'large'

  // Whether the input has pill (fully rounded) styling.
  pill?: boolean

  // Whether the field is disabled.
  disabled?: boolean

  // Whether the field is required.
  required?: boolean | string

  // Whether the user can select one or many options.
  multi?: boolean

  // Whether the field has an error.
  hasErrors?: boolean

  // Item property to use when displaying items in the dropdown.
  labelProp?: string

  // Item property to use as the internal item value (and as an id).
  valueProp?: string

  // Item property to use when searching for items. Default to `labelProp`.
  searchableProp?: string

  // Item property to use when displaying item descriptions in the dropdown.
  descriptionProp?: string

  // Item property to use when dropdown is closed and item is selected.
  selectedProp?: string

  // Whether the whole items should be propagated on a change event.
  // This also determines whether what is received/stored when using v-model.
  wholeItem?: boolean

  // Whether the input should allow to create new items.
  allowCreate?: boolean

  // Function to format the label for new items.
  labelForValue?: ((value: any) => string)

  // Label to show to unselect an item
  unselectLabel?: string

  // Whether the input should not display an option to unselect an item
  noUnselect?: boolean

  // Whether the input should accept only numeric values
  numeric?: boolean

  // A placeholder to display in the text input when no values are selected.
  placeholder?: string

  // Ignore the input type of the field provided by <Input>.
  type?: string

  // Path helper used to fetch remote options
  fetchOptions?: PathHelper

  // Whether to include the currently selected item(s) in the options.
  includeSelectedInOptions?: boolean

  // Whether we should collapse the dropdown and clear the input after selecting an item
  collapseOnSelect?: boolean

  // Options to display when the query is empty.
  emptyQueryOptions?: any[]

  // Disable typeahead / filtering
  disableFiltering?: boolean
}>()

const emit = defineEmits(['update:modelValue', 'focus', 'blur'])

const slots = useSlots()

let expanded = $ref(false)
let filteringBy = $ref('')

// Items that the user might have created in `allowCreate` mode.
const createdItems = $ref<any[]>([])

// The items that are selected.
let selectedItems = $ref<any[]>([])

let remoteItems = $ref<any[]>()

let fetchingRemoteItems = $ref(false)

// Label of the selected item, if any.
const selectedItemLabel = $computed(() => selectedItem?.[selectedProp || labelProp])

// Placeholder of the select input.
const placeholder = $computed(() => (expanded && selectedItemLabel)
  || (!multi || selectedItems.length === 0 ? originalPlaceholder : ''))

// Returns the value for the text input.
// When the input is closed, it displays the selected item label.
const searchInputValue = $computed(() => expanded
  ? filteringBy
  : selectedItemLabel,
)

// The specified items, plus any extra items that the user has
// created in the allowCreate mode.
const itemsAndCreated = $computed(() => {
  // When using wholeItem, the selected items can be displayed immediately.
  const availableItems = (
    options
    || (wholeItem && value && (multi ? value : [value]))
    || []
  ).concat(emptyQueryOptions)

  return allowCreate ? createdItems.concat(availableItems) : availableItems
})

// Filter items according to the current user input.
// In multi-select mode it doesn't display currently selected items.
const filteredItems = $computed(() => {
  const notSelected = (item: any) => includeSelectedInOptions ? true : !findItemForValue(selectedItems, item[valueProp])

  if (fetchOptions) return remoteItems?.filter(notSelected) || []

  const items = multi ? itemsAndCreated.filter(notSelected) : itemsAndCreated
  return filterItems(items, { query: filteringBy, searchableProp: searchableProp ?? labelProp })
})

// Returns the items that will be displayed in the dropdown.
const displayedItems = $computed(() => [
  ...((!filteringBy.length && !filteredItems.length) ? emptyQueryOptions : []),
  displayNoSelectionItem && noSelectionItem,
  displayCreateItem && createItem,
  ...filteredItems,
].filter(x => x))

// A new item can be created from a non-empty value in the input.
const displayCreateItem = $computed(() =>
  allowCreate && isPresent(filteringBy)
  && !itemsAndCreated.find((item: any) => item[labelProp] === filteringBy || item[valueProp] === filteringBy)
  && !remoteItems?.find((item: any) => item[labelProp] === filteringBy || item[valueProp] === filteringBy),
)

// A blank option to unselect the current item will be displayed.
const displayNoSelectionItem = $computed(() => selectedItem && !(noUnselect || required))

const labelForValue = originalLabelForValue || identity

// A fake item that allows to create a new item based on the input.
const createItem = $computed(() => {
  return {
    [labelProp]: labelForValue(filteringBy),
    creatable: true,
  }
})

// A fake item that allows to unselect the current item.
const noSelectionItem = $computed(() => ({ [labelProp]: unselectLabel, unselect: true }))

// Whether a single item can be selected. Single-selection mode.
const singleSelection = $computed(() => !multi)

// The selected item in single-selection mode.
const selectedItem = $computed(() => singleSelection && selectedItems[0])

// Helper function to extract the inner value from the `value` prop
// by taking into account the `wholeItem` setting
const extractValue = wholeItem ? (item: any) => item[valueProp] : (value: any) => value

// Public: Indicates if the no items section should be displayed. Occurs if:
//   - There are no items.
//   - Displaying "Add…" or "Unselect" and a `noItems` slot was provided.
const shouldDisplayNoItems = $computed(() => !fetchingRemoteItems && (displayedItems.length === 0
  || ((displayCreateItem || displayNoSelectionItem) && displayedItems.length === 1 && slots.noItems)),
)

// Synchronizes selectedItems with the specified value and items.
watchEffect(() => {
  selectedItems = isNil(value) ? [] : getSelectedItemsFrom(value)
})

// Remove non-significant decimals added by the backend.
function coerceValue (value: any) {
  return numeric ? removeDecimals(value) : value
}

// Calculates the selectedItems based on the specified value.
function getSelectedItemsFrom (valueRaw: any) {
  const value = coerceValue(valueRaw)

  // Convert to Array to process all cases in the same way.
  const values = multi ? [...value] : [value]

  const findItemFor = (itemOrValue: any) =>
    findItemForValue(itemsAndCreated, extractValue(itemOrValue))
      // When doing a remote typeahead search the selected items aren't in the options (itemsAndCreated).
      // They are in the remoteOptions ref, that gets cleared after selecting a remote option.
      // So we need to preserve the items even if they aren't find in the options.
      || (fetchOptions && findItemForValue(selectedItems, extractValue(itemOrValue)))

  // It's more performant to iterate on the values and use find to short-circuit.
  return values.map(value => findItemFor(value) || (allowCreate && createNewItem(value))).filter(Boolean)
}

// Helper: Returns a new item based on the current user input.
function createNewItem (value: any) {
  const newItem = wholeItem && isObject(value)
    ? value
    : { [labelProp]: labelForValue(value), [valueProp]: coerceValue(value) }
  createdItems.push(newItem)
  return newItem
}

function findItemForValue (items: any[], value: any) {
  return items.find(itemToCheck => isEqual(
    String(itemToCheck[valueProp]).toLowerCase(),
    String(value).toLowerCase()),
  )
}

// Propagates the input event. The value type is determined by `multi`.
function notifyValueChange () {
  const itemToValue = wholeItem ? (item: any) => item : (item: any) => item[valueProp]
  const values = selectedItems.map(itemToValue).map(coerceValue)
  emit('update:modelValue', multi ? values : values[0])
}

const el = $(useCurrentElement<HTMLElement>())
const dropdownEl = $ref<HTMLElement>(undefined as any)
const textInput = $ref<typeof GTextInput>(undefined as any)

onClickOutside($$(el), collapseDropdown)
useResizeObserver($$(dropdownEl), repositionDropdown)

// Calculates the position of the dropdown respective to the input.
async function repositionDropdown () {
  const { x, y } = await computePosition(el, dropdownEl, {
    middleware: [flip(), offset(8)],
  })
  Object.assign(dropdownEl.style, { top: `${y}px`, left: `${x}px` })
}

// Expands a dropdown that displays the available items.
function expandDropdown () {
  if (disabled) return

  if (selectedItem)
    focusItem(displayedItems.findIndex(item => item[valueProp] === selectedItem[valueProp]))
  else
    focusFirstItem()

  expanded = true
  nextTick(repositionDropdown)
  scrollFocusedItemIntoView()

  if (!multi)
    textInput.select()

  emit('focus')
}

// Closes the dropdown that displays the available items.
function collapseDropdown () {
  if (!expanded) {
    textInput.blur()
    return
  }

  filteringBy = ''
  expanded = false
}

// Allows to expand or close the menu from the toggle.
function toggleDropdown () {
  expanded ? collapseDropdown() : expandDropdown()
}

// Unselects the last item when the search input is empty.
function onDeletePressed () {
  if (!filteringBy && !noUnselect) {
    const lastSelectedItem = selectedItems.at(-1)
    if (lastSelectedItem) unselectItem(lastSelectedItem)
  }
  if (!expanded)
    return expandDropdown()
}

// In single-select mode the dropdown is closed after selection.
function selectItemSingle (item: any) {
  selectedItems = item.unselect ? [] : [item]
}

// In multi-select mode the input remains focused after selection.
function selectItemMulti (item: any) {
  if (!findItemForValue(selectedItems, item)) selectedItems.push(item)

  // Select the closest option, necessary if the user selected the last one.
  focusedItemIndex = Math.min(displayedItems.length - 1, focusedItemIndex)
}

// Selects the specified item and notifies the change.
function selectItem (option: any, collapse?: boolean) {
  const item = option.creatable ? createNewItem(filteringBy) : option

  if (multi)
    selectItemMulti(item)
  else
    selectItemSingle(item)

  if (collapse)
    collapseDropdown()

  if (collapseOnSelect)
    filteringBy = ''

  notifyValueChange()
}

// Removes a selected item.
function unselectItem (item: any) {
  const removedItem = removeBy(selectedItems, selectedItem => selectedItem[valueProp] === item[valueProp])
  if (removedItem) notifyValueChange()
  nextTick(collapseDropdown)
}

// Keeps track of the focused item in the dropdown to easily update it when
// navigating with the keyboard.
let focusedItemIndex = $ref(-1)
watch($$(filteringBy), focusFirstItem)

if (fetchOptions) {
  watch(
    () => filteringBy,
    () => debouncedfetchOptions(filteringBy),
  )
}

// Focuses the first actual element, unless there are none in which case it
// tries to focus the item added in `allowCreate` mode.
function focusFirstItem () {
  const firstItemIndex = [displayCreateItem, displayNoSelectionItem].filter(x => x).length
  focusedItemIndex = Math.min(firstItemIndex, displayedItems.length - 1)
}

function scrollFocusedItemIntoView () {
  nextTick(() => {
    const focusedElement = el.querySelector<HTMLElement>('.select-item.focused')
    if (focusedElement)
      scrollIntoView(focusedElement, { animate: false })
  })
}

// Select the next item when navigating with the down arrow key.
function focusNextItem () {
  focusedItemIndex = Math.min(displayedItems.length - 1, focusedItemIndex + 1)
  scrollFocusedItemIntoView()
}

// Select the previous item when navigating with the up arrow key.
function focusPreviousItem () {
  focusedItemIndex = Math.max(0, focusedItemIndex - 1)
  scrollFocusedItemIntoView()
}

// Focuses the hovered item.
function focusItem (index: number) {
  focusedItemIndex = index
}

// Selects the item that is currently focused (mouse or keyboard).
function selectFocusedItem (event: Event, preventDefault = true) {
  if (!expanded) {
    if (preventDefault) { event.preventDefault() }
    expandDropdown()
    return
  }

  const focusedItem = focusedItemIndex > -1 && displayedItems[focusedItemIndex]
  if (focusedItem) {
    if (preventDefault) { event.preventDefault() }
    selectItem(focusedItem, !multi)
  }
}

function itemKey (item: any, index: number) {
  const firstPart = () => {
    if (item.creatable) return `creatable-${item[labelProp]}`
    if (item.unselect) return '$unselect$'
    return item[valueProp]
  }
  return firstPart() + (focusedItemIndex === index ? '-focused' : '')
}

const debouncedfetchOptions = typeaheadDebounce(
  async (term: string) => {
    if (!term.length) {
      remoteItems = undefined
      fetchingRemoteItems = false
      return
    }

    fetchingRemoteItems = true

    try {
      remoteItems = await fetchOptions?.({ query: { term }, visit: false })
      focusFirstItem()
    }
    catch (error) {
      console.error('Error fetching remote items:', error)
      return []
    }

    fetchingRemoteItems = false
  },
)

// Displayed when no items are available in `allowCreate`-mode.
const createItemMessage = 'Type to create a new item…'

// Displayed when no items are available in the dropdown.
const emptyItemsMessage = 'No items available'

defineExpose({
  focus: () => textInput?.focus(),
})
</script>

<template>
  <GTextInput
    ref="textInput"
    :wrapperClass="['select-input', { disabled, expanded, multi, 'z-select-expanded': expanded }]"
    :hasErrors="hasErrors"
    :disabled="disabled"
    :modelValue="searchInputValue"
    :type="numeric ? 'number' : 'text'"
    :placeholder="placeholder"
    :focusInput="toggleDropdown"
    :pill="pill"
    :size="size"
    :readonly="disableFiltering"
    :class="{
      'cursor-pointer': disableFiltering
    }"
    @focus="expandDropdown"
    @pointerdown="!expanded && expandDropdown()"
    @update:modelValue="expanded ? filteringBy = $event : expandDropdown()"
    @keydown.enter="selectFocusedItem"
    @keydown.delete="onDeletePressed"
    @keydown.tab="selectFocusedItem($event, false)"
    @keydown.esc.prevent="collapseDropdown"
    @keydown.down.prevent="focusNextItem"
    @keydown.up.prevent="focusPreviousItem"
  >
    <template v-if="multi" #inner>
      <slot name="inner">
        <GBadge
          v-for="item in selectedItems"
          :key="`${item[labelProp]}-${item[valueProp]}`"
          class="selected-item"
          type="chip"
          :closable="!disabled"
          :label="item[labelProp]"
          size="md"
          @close="unselectItem(item)"
          @click="unselectItem(item)"
        />
      </slot>
    </template>
    <template v-else #inner>
      <slot name="inner" :selectedItem="selectedItem" :filteringBy="filteringBy"/>
    </template>

    <template #suffix>
      <slot name="suffix">
        <slot name="preSuffix"/>
        <div class="absolute inset-y-0 right-1 flex items-center p-2">
          <GIcon
            v-if="singleSelection"
            class="select-input-arrow pointer-events-none"
            :class="{ 'opacity-20': disabled, 'text-red-400': hasErrors }"
            style="top: 14px;"
            :icon="faChevronDown"
            size="xs"
          />
        </div>
      </slot>
      <Transition appear name="fade-and-grow">
        <div
          v-show="expanded"
          ref="dropdownEl"
          class="input-items-dropdown absolute py-1
            rounded-md border-1 bg-white drop-shadow-dropdown z-40"
        >
          <GSelectInputOption v-if="fetchingRemoteItems" :size="size">
            <template #label>
              <LoadingIndicator/>
            </template>
          </GSelectInputOption>

          <GSelectInputOption
            v-for="item, index in displayedItems"
            v-else
            :key="itemKey(item, index)"
            :item="item"
            :labelProp="labelProp"
            :descriptionProp="descriptionProp"
            :filteringBy="filteringBy"
            :size="size"
            :class="{ focused: focusedItemIndex === index }"
            @mousemove.passive="focusItem(index)"
            @click.stop.prevent="selectItem(item, collapseOnSelect)"
          >
            <template #pre>
              <slot name="itemPre" :item="item" :filteringBy="filteringBy"/>
            </template>
            <template #post>
              <slot name="itemPost" :item="item" :filteringBy="filteringBy"/>
            </template>
          </GSelectInputOption>

          <GSelectInputOption v-show="shouldDisplayNoItems" class="empty italic">
            <template #label>{{ allowCreate ? createItemMessage : emptyItemsMessage }}</template>
          </GSelectInputOption>
        </div>
      </Transition>
    </template>
  </GTextInput>
</template>

<style lang="scss" scoped>
.select-input {
  --cursor: pointer;

  :deep(.g-inner-input) {
    width: 80px;
  }
}

.disabled {
  --cursor: default;
}

.select-input-arrow {
  transition: transform 0.1s ease-out;

  .select-input.expanded & {
    transform: translateY(2px) rotate(180deg);
  }
}

.input-items-dropdown {
  --br: theme("colors.grey.primary");

  border: 1px solid var(--br);
  max-height: 200px;
  min-width: calc(100% + 2px * 2);
  overflow-y: auto;
  right: -2px;
  width: min-content;
}
</style>
